import { AloftDate, MLSID, expected, noop } from '@aloft/toolkit-shared';
import { Modal } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useAutofillProperty } from '../../../../hooks/useAutofillProperty.ts';
import { ToastOptions } from '../../../../hooks/useToaster/types.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { addComps } from '../../../../redux/features/actions/addComps.ts';
import {
  selectEffectiveDate,
  selectHasArvOption,
  selectHasRentalConfiguration,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { useUpdateOrderFeatures } from '../../../orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.ts';
import { AddCompsForm } from './internal/AddCompsForm.tsx';
import { AddComparablesForm, schAddComparablesForm } from './types.ts';

export const AddComparablesModal = ({
  onModalClose,
  isModalOpen,
  listingType,
}: {
  onModalClose: () => void;
  isModalOpen: boolean;
  listingType?: 'listing' | 'rentalListing';
}) => {
  const dispatch = useAppDispatch();
  const autofillProperty = useAutofillProperty();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const hasRentalConfiguration = useAppSelector(selectHasRentalConfiguration);
  const orderID = useAppSelector(selectOrder).id;

  const resolvedListingType = listingType ?? (hasRentalConfiguration ? 'rentalListing' : undefined);

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToaster();

  const autofillForm = useZodForm({
    schema: schAddComparablesForm,
    defaultValues: {
      entries: [{ method: 'mls', mls: '', mlsListingID: '' }],
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const submitAutofill = autofillForm.handleSubmit(async (submitParams: AddComparablesForm) => {
    setIsLoading(true);
    const successToastOptions: ToastOptions = {
      type: 'success' as const,
      id: 'add-comps-via-import',
      title: 'Successfully added property!',
    };

    const handleCompletion = (toastOptions: ToastOptions) => {
      toast(toastOptions);
      setIsLoading(false);
      if (toastOptions.type === 'success') {
        onModalClose();
      }
    };

    try {
      const properties = await Promise.all(
        submitParams.entries.map((entry) => {
          if (entry.method === 'mls') {
            return autofillProperty({
              mlsListingInfo: {
                mls: expected(entry.mls) as MLSID,
                mlsListingID: expected(entry.mlsListingID),
              },
              effectiveDate: AloftDate.schema.parse(expected(effectiveDate)).toJSON(),
              orderID,
              compType: submitParams.compType,
              listingType: resolvedListingType,
            });
          }
          return autofillProperty({
            address: expected({
              AddressLineText: entry.AddressLineText,
              unitNumber: entry.unitNumber,
              CityName: entry.CityName,
              StateCode: entry.StateCode,
              PostalCode: entry.PostalCode,
            }),
            effectiveDate: AloftDate.schema.parse(expected(effectiveDate)).toJSON(),
            orderID,
            compType: submitParams.compType,
            listingType: resolvedListingType,
          });
        }),
      );

      dispatch(
        addComps(
          resolvedListingType === 'rentalListing'
            ? { comps: properties, sequenceTarget: 'rentalCompSequence' }
            : properties,
        ),
      );
      updateOrderFeatures(orderID, properties).then(noop).catch(console.error);
    } catch (e: unknown) {
      let errorTitle = 'Failed to add property.';
      if (e instanceof Error && e.message.includes('No property found from MLS listing')) {
        errorTitle = 'We could not find a property from the MLS listing info you provided.';
      }

      handleCompletion({
        type: 'error',
        id: 'add-comps-via-import-error',
        title: errorTitle,
      });

      return undefined;
    }

    return handleCompletion(successToastOptions);
  }, console.error);

  const autofillFields = autofillForm.watch();

  const isAutofillValid = () => {
    const { entries, compType } = autofillFields;
    if (!entries?.length) return false;

    const isValuationTypeValid = hasArvOption ? !!compType : true;
    if (!isValuationTypeValid) return false;

    return entries.every((entry) => {
      if (entry.method === 'mls') {
        return !!entry.mls && !!entry.mlsListingID;
      }
      return !!entry.AddressLineText && !!entry.CityName && !!entry.StateCode && !!entry.PostalCode;
    });
  };

  return (
    <Modal
      size="lg"
      isOpen={isModalOpen}
      onClose={onModalClose}
      closeOnEsc
      title="Manually Add Comps"
      primaryAction={{
        id: 'add-btn',
        text: 'Add',
        onClick: () => {
          submitAutofill();
        },
        disabled: !isAutofillValid(),
        isLoading,
      }}
      secondaryAction={{
        id: 'cancel-btn',
        text: 'Cancel',
        onClick: () => {
          autofillForm.reset();
          onModalClose();
        },
      }}
    >
      <AddCompsForm form={autofillForm} />
      {autofillForm.formState.errors.entries?.message && (
        <p>{autofillForm.formState.errors.entries.message}</p>
      )}
    </Modal>
  );
};
